import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode
} from 'react';
import { useLocation, useNavigate, useNavigationType, type Location } from 'react-router-dom';
import { useIsDesktop, useReducedMotion } from '../../../lib/motion';
import { ScreenLocationProvider } from '../../../lib/screenLocation';
import { isTabRoot, isDarkRoute, IMMERSIVE_DARK_SURFACE } from '../appNav';
import { cn } from '../../../lib/cn';
import { DIM_MAX, historyIdx, PARALLAX } from './constants';
import type { Drag, RenderLayer, Transition } from './types';
import { useDeclarativeTransition } from './useDeclarativeTransition';
import { useEdgeSwipeBack } from './useEdgeSwipeBack';

/**
 * The app's native navigation stack. Renders the routed content as layered
 * screens and animates between them with **compositor-driven CSS transforms** —
 * the same off-main-thread technique that makes the edge-swipe-back feel buttery —
 * instead of a main-thread JS animation loop. Three things make it read native:
 *
 *  1. **Prepare-then-animate.** A pushed/popped screen is mounted and painted at
 *     its off-screen start position *first*, then slid into place on a later frame.
 *     The expensive mount (component tree + Firestore listeners + first paint) is
 *     paid before the slide, so the slide itself is pure GPU and never drops frames
 *     while the incoming screen is still mounting.
 *  2. **Reveal underneath.** A back navigation (button *or* interactive swipe)
 *     keeps the destination screen mounted *below* the one leaving, with the iOS
 *     parallax + dim peek — so you see where you're going the whole way, never a
 *     blank page.
 *  3. **One owner for both.** The interactive edge-swipe drives the very same two
 *     layers, then hands off to the router on commit, so there's never a double
 *     animation or a z-index fight.
 *
 * Honours Reduce Motion and desktop (both fall back to a cross-fade). `children`
 * receives a frozen per-layer location so an exiting screen keeps rendering its
 * own route while it animates away.
 */
export function NavStack({
  children,
  className,
  interactive = false
}: {
  children: (location: Location) => ReactNode;
  className?: string;
  /** Enable the interactive edge-swipe-to-go-back (mobile content viewport only). */
  interactive?: boolean;
}) {
  const loc = useLocation();
  const navType = useNavigationType();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const isDesktop = useIsDesktop();

  const [transition, setTransition] = useState<Transition | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);
  const nodesRef = useRef(new Map<string, HTMLDivElement>());
  const dimRef = useRef<HTMLDivElement | null>(null);

  const prevKeyRef = useRef(loc.key);
  const prevLocRef = useRef(loc);
  const prevIdxRef = useRef(historyIdx());
  const seqRef = useRef(0);
  const animIdRef = useRef(0);

  // Live mirrors for the (once-bound) gesture listeners.
  const curKeyRef = useRef(loc.key);
  const navRef = useRef(navigate);
  const busyRef = useRef(false); // a declarative transition is animating
  const animatingRef = useRef(false); // the gesture's release settle is animating
  const committingRef = useRef(false); // gesture pop committed → adopt new route silently
  const historyRef = useRef(new Map<number, Location>());
  curKeyRef.current = loc.key;
  navRef.current = navigate;

  // Remember each history entry's full location so a back-swipe knows which screen
  // sits beneath (react-router only hands us the *current* one).
  const idxNow = historyIdx();
  historyRef.current.set(idxNow, loc);
  if (historyRef.current.size > 60) {
    const oldest = historyRef.current.keys().next().value;
    if (oldest !== undefined) historyRef.current.delete(oldest);
  }

  // Classify the navigation the moment the location changes (during render, so the
  // entering layer paints at its start position on the very same commit — an effect
  // would lag by a frame and flash). This is the supported "adjust state when an
  // input changes" pattern: the guard makes the immediate re-render bail out.
  if (loc.key !== prevKeyRef.current) {
    if (committingRef.current) {
      // The gesture already animated this exact pop; adopt the new route with no
      // declarative transition on top of it.
      if (transition !== null) setTransition(null);
    } else {
      const pathnameOnly = prevLocRef.current.pathname === loc.pathname;
      if (pathnameOnly) {
        // Same screen, new search params — hub tab switches, deep-linked builder
        // opens, etc. In-screen motion (segmented control, card-builder sheet)
        // owns the transition; a full-page cross-fade here fights it and reads janky.
        if (transition !== null) setTransition(null);
      } else {
        const tabSwitch = isTabRoot(prevLocRef.current.pathname) && isTabRoot(loc.pathname);
        if (tabSwitch) {
          // Bottom-tab / side-rail lateral switch — instant cut (UITabBarController
          // style). A cross-fade stacked two mismatched layouts (Standings vs Play)
          // and compounded opacity with in-screen SegmentedViews enter motion.
          if (transition !== null) setTransition(null);
        } else {
          const prevIdx = prevIdxRef.current;
          const dir = idxNow === prevIdx ? 0 : idxNow > prevIdx ? 1 : -1;
          const mode =
            reduce || isDesktop || navType === 'REPLACE' || dir === 0 ? 'fade' : 'slide';
          setTransition({
            id: ++seqRef.current,
            mode,
            dir: dir === 0 ? 1 : dir,
            leaving: { key: prevKeyRef.current, location: prevLocRef.current }
          });
        }
      }
    }
    prevKeyRef.current = loc.key;
    prevLocRef.current = loc;
    prevIdxRef.current = idxNow;
  }

  useEffect(() => {
    busyRef.current = transition != null;
  }, [transition]);

  useDeclarativeTransition(
    transition,
    setTransition,
    loc.key,
    rootRef,
    nodesRef,
    dimRef,
    animIdRef
  );

  useEdgeSwipeBack(
    interactive,
    rootRef,
    nodesRef,
    dimRef,
    curKeyRef,
    navRef,
    busyRef,
    animatingRef,
    committingRef,
    historyRef,
    setDrag
  );

  // Park the freshly-mounted destination at its parallax start position the instant
  // a back-swipe arms it, so the first finger move reveals an already-painted screen.
  // (Skip once the pop has committed — `target` and the live route are then the same
  // node, and re-parking it would yank the just-revealed screen back off to the left.)
  useLayoutEffect(() => {
    if (!drag || committingRef.current || drag.target.key === loc.key) return;
    const top = nodesRef.current.get(loc.key) ?? null;
    const under = nodesRef.current.get(drag.target.key) ?? null;
    const dim = dimRef.current;
    const w = rootRef.current?.clientWidth || window.innerWidth;
    if (top) {
      top.style.transition = 'none';
      top.style.transform = 'translateX(0px)';
      top.style.willChange = 'transform';
    }
    if (under) {
      under.style.transition = 'none';
      under.style.transform = `translateX(${-PARALLAX * w}px)`;
      under.style.willChange = 'transform';
    }
    if (dim) {
      dim.style.transition = 'none';
      dim.style.opacity = String(DIM_MAX);
    }
  }, [drag, loc.key]);

  // ── Build the layer plan ───────────────────────────────────────────────────────
  /** Pathname-stable key for every layer that should survive a transition settling.
   *  The animating `top` screen keeps `location.key` so two entries never collide
   *  mid-push; `under` + `solo` share pathname so the revealed screen is not
   *  remounted when a pop finishes (that remount was the post-back blink). */
  const reactKey = (layer: RenderLayer) =>
    layer.role === 'top' ? layer.key : layer.location.pathname;

  let layers: RenderLayer[];
  if (committingRef.current) {
    // Gesture pop just committed — show only the adopted route (reuses the revealed
    // node by key, so no flash).
    layers = [{ key: loc.key, location: loc, role: 'solo', z: 1 }];
  } else if (drag) {
    layers = [
      { key: drag.target.key, location: drag.target, role: 'under', z: 0 },
      { key: loc.key, location: loc, role: 'top', z: 1 }
    ];
  } else if (transition && transition.leaving.key !== loc.key) {
    const pop = transition.mode === 'slide' && transition.dir < 0;
    layers = pop
      ? [
          { key: loc.key, location: loc, role: 'under', z: 0 },
          { key: transition.leaving.key, location: transition.leaving.location, role: 'top', z: 1 }
        ]
      : [
          {
            key: transition.leaving.key,
            location: transition.leaving.location,
            role: 'under',
            z: 0
          },
          { key: loc.key, location: loc, role: 'top', z: 1 }
        ];
  } else {
    layers = [{ key: loc.key, location: loc, role: 'solo', z: 1 }];
  }

  return (
    <div ref={rootRef} className={cn('relative overflow-hidden', className)}>
      {layers.map((layer) => {
        const isCurrent = layer.key === loc.key;
        return (
          <div
            key={reactKey(layer)}
            ref={(el) => {
              if (el) nodesRef.current.set(layer.key, el);
              else nodesRef.current.delete(layer.key);
            }}
            aria-hidden={isCurrent ? undefined : true}
            className={cn(
              'absolute inset-0 h-full w-full overflow-hidden md:bg-canvas',
              isDarkRoute(layer.location.pathname) ? IMMERSIVE_DARK_SURFACE : 'bg-white'
            )}
            style={{ zIndex: layer.z, pointerEvents: isCurrent ? undefined : 'none' }}
          >
            <ScreenLocationProvider value={layer.location}>
              {children(layer.location)}
            </ScreenLocationProvider>
            {layer.role === 'under' && (
              <div
                ref={(el) => {
                  dimRef.current = el;
                }}
                aria-hidden
                className="pointer-events-none absolute inset-0 z-[2] bg-black"
                style={{ opacity: 0 }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
